import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import InvDataGrid from '../../components/InvDataGrid';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { InvTextField, InvSelect } from '../../components/InvField';
import MaterialMasterShell from '../../components/master/MaterialMasterShell';
import MasterListShell from '../../components/master/MasterListShell';
import FilterToolbar from '../../components/FilterToolbar';
import MasterFormDialog from '../../components/master/MasterFormDialog';
import ActiveStatusBadge from '../../components/master/ActiveStatusBadge';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../api/inventoryMaster';
import { getItemClasses } from '../../api/itemClass';
import { CATEGORY_TYPES, CATEGORY_PAGE_SIZE, STATUS_FILTER_OPTIONS } from '../../constants';
import {
  normalizeCategoryList, normalizeCategory, categoryRowId,
  resolveParentLabel, toInventoryPage,
  normalizeItemClassList, itemClassRowId, toSelectOptions,
} from '../../utils/masterEntity';
import { formatDate } from '../../utils';
import { IPD_COLORS, cardSx, inputSx, switchSx } from '../../theme';
import useMasterList from '../../hooks/useMasterList';

const EMPTY_FORM = {
  category_code: '',
  category_name: '',
  item_class_id: '',
};

const EDIT_FORM = {
  category_name: '',
  description: '',
  is_active: true,
};

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Types' },
  ...CATEGORY_TYPES.map(t => ({ value: t, label: t })),
];

function BoolCell({ value }) {
  return value
    ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.success.text, fontSize: 18 }} />
    : <CancelIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 18 }} />;
}

function SwitchField({ name, label, control: formControl }) {
  return (
    <Controller
      name={name}
      control={formControl}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Switch
              checked={!!field.value}
              onChange={e => field.onChange(e.target.checked)}
              sx={switchSx}
            />
          }
          label={label}
          sx={{ color: IPD_COLORS.textPrimary, m: 0 }}
        />
      )}
    />
  );
}

function buildListParams({ page, search, typeFilter, statusFilter, pageSize }) {
  const params = { limit: pageSize, offset: page * pageSize };
  if (search.trim()) params.search = search.trim();
  if (typeFilter) params.category_type = typeFilter;
  if (statusFilter === 'active') params.is_active = true;
  if (statusFilter === 'inactive') params.is_active = false;
  return params;
}

function buildCreateBody(data) {
  return {
    category_code: data.category_code.trim().toUpperCase(),
    category_name: data.category_name.trim(),
    item_class_id: data.item_class_id,
    category_type: 'GENERAL',
    parent_category_id: null,
    description: '',
    is_drug_category: false,
    batch_required_default: false,
    expiry_required_default: false,
    serial_required_default: false,
  };
}

function buildUpdateBody(data) {
  return {
    category_name: data.category_name.trim(),
    description: data.description?.trim() || '',
    is_active: data.is_active !== false,
  };
}

function mapRowToEditForm(row) {
  return {
    category_name: row?.category_name ?? '',
    description: row?.description ?? '',
    is_active: row?.is_active !== false,
  };
}

export default function CategoryList() {
  const list = useMasterList({ pageSize: CATEGORY_PAGE_SIZE });
  const {
    items, setItems, totalCount, setTotalCount,
    loading, setLoading, error, setError,
    page, setPage, searchInput, setSearchInput,
    search, statusFilter, setStatusFilter,
    resetFilters, pageSize,
  } = list;

  const [typeFilter, setTypeFilter] = useState('');
  const [allCategories, setAllCategories] = useState([]);
  const [itemClasses, setItemClasses] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: EMPTY_FORM });
  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm({ defaultValues: EDIT_FORM });

  const hasActiveFilters = Boolean(search.trim() || typeFilter || statusFilter);

  const loadAllCategories = useCallback(async () => {
    try {
      const data = await getCategories({ limit: 200, is_active: true });
      setAllCategories(normalizeCategoryList(data));
    } catch {
      setAllCategories([]);
    }
  }, []);

  const loadItemClasses = useCallback(async () => {
    try {
      const data = await getItemClasses({ limit: 200, is_active: true });
      setItemClasses(normalizeItemClassList(data));
    } catch {
      setItemClasses([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getCategories(buildListParams({ page, search, typeFilter, statusFilter, pageSize }));
      const { items: fetched, total } = toInventoryPage(raw, { page, pageSize });
      setItems(fetched.map(normalizeCategory));
      setTotalCount(total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter, pageSize, setLoading, setError, setItems, setTotalCount]);

  useEffect(() => { loadAllCategories(); }, [loadAllCategories]);
  useEffect(() => { loadItemClasses(); }, [loadItemClasses]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editTarget) resetEdit(mapRowToEditForm(editTarget));
  }, [editTarget, resetEdit]);

  const itemClassOptions = useMemo(() => toSelectOptions(itemClasses, {
    idKey: 'item_class_id', nameKey: 'class_name', codeKey: 'class_code',
  }), [itemClasses]);

  const resolveItemClassName = useCallback((row) => {
    if (!row) return '—';
    if (row.item_class_name) return row.item_class_name;
    const itemClass = itemClasses.find(
      c => String(itemClassRowId(c)) === String(row.item_class_id),
    );
    return itemClass?.class_name ?? '—';
  }, [itemClasses]);

  const columns = useMemo(() => [
    { field: 'category_code', headerName: 'Code', width: 100 },
    { field: 'category_name', headerName: 'Name', flex: 1, minWidth: 160 },
    {
      field: 'item_class_id',
      headerName: 'Item Class',
      width: 150,
      valueGetter: (p) => resolveItemClassName(p.row),
    },
    {
      field: 'material_group_name',
      headerName: 'Material Group',
      width: 150,
      valueGetter: (p) => p.row.material_group_name || '—',
    },
    {
      field: 'parent_category_id',
      headerName: 'Parent',
      width: 150,
      valueGetter: (p) => resolveParentLabel(p.row, allCategories, {
        idKey: 'category_id',
        nameKey: 'category_name',
        parentKey: 'parent_category_id',
      }),
    },
    { field: 'category_type', headerName: 'Type', width: 110 },
    {
      field: 'is_drug_category',
      headerName: 'Drug',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => <BoolCell value={p.value} />,
    },
    {
      field: 'batch_required_default',
      headerName: 'Batch',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => <BoolCell value={p.value} />,
    },
    {
      field: 'expiry_required_default',
      headerName: 'Expiry',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => <BoolCell value={p.value} />,
    },
    {
      field: 'serial_required_default',
      headerName: 'Serial',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => <BoolCell value={p.value} />,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (p) => <ActiveStatusBadge active={p.value} />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 100,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit category">
            <IconButton size="small" onClick={() => setEditTarget(p.row)} sx={{ color: IPD_COLORS.primary }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.is_active !== false && (
            <Tooltip title="Deactivate category">
              <IconButton size="small" onClick={() => setDeactivateTarget(p.row)} sx={{ color: IPD_COLORS.error.text }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [allCategories, resolveItemClassName]);

  function openAddModal() {
    reset(EMPTY_FORM);
    setAddOpen(true);
  }

  function closeAddModal() {
    if (saving) return;
    setAddOpen(false);
    reset(EMPTY_FORM);
  }

  function closeEditModal() {
    setEditTarget(null);
    resetEdit(EDIT_FORM);
  }

  function handleResetFilters() {
    resetFilters();
    setTypeFilter('');
  }

  async function onSubmit(data) {
    setSaving(true);
    try {
      await createCategory(buildCreateBody(data));
      closeAddModal();
      toast.success('Category created');
      await Promise.all([loadAllCategories(), load()]);
    } catch (e) {
      toast.error(e?.message || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  }

  async function onEditSubmit(data) {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await updateCategory(categoryRowId(editTarget), buildUpdateBody(data));
      closeEditModal();
      toast.success('Category updated');
      await Promise.all([loadAllCategories(), load()]);
    } catch (e) {
      toast.error(e?.message || 'Failed to update category');
    } finally {
      setEditSaving(false);
    }
  }

  async function confirmDeactivate() {
    setDeactivating(true);
    try {
      await deleteCategory(categoryRowId(deactivateTarget));
      setDeactivateTarget(null);
      toast.success('Category deactivated');
      await Promise.all([loadAllCategories(), load()]);
    } catch (e) {
      toast.error(e?.message || 'Failed to deactivate category');
      setDeactivateTarget(null);
    } finally {
      setDeactivating(false);
    }
  }

  const listSubtitle = hasActiveFilters
    ? 'Showing filtered results — adjust or reset filters to see all categories.'
    : 'Browse and manage categories used to classify inventory items.';

  return (
    <MaterialMasterShell
      title="Category Master"
      description="Organize items into categories for classification, tracking defaults, and reporting."
      badge={`${totalCount} ${totalCount === 1 ? 'category' : 'categories'}`}
      actions={
        <InvButton startIcon={<AddIcon />} onClick={openAddModal}>
          Add Category
        </InvButton>
      }
    >
      <MasterListShell
        title="All Categories"
        subtitle={listSubtitle}
        filters={(
          <>
            <FilterToolbar.Search
              placeholder="Search by name or code..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <FilterToolbar.Filter
              label="Type"
              value={typeFilter}
              onChange={e => { setTypeFilter(e.target.value); setPage(0); }}
              options={TYPE_FILTER_OPTIONS}
            />
            <FilterToolbar.Filter
              label="Status"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              options={STATUS_FILTER_OPTIONS}
            />
            <FilterToolbar.Reset onClick={handleResetFilters} disabled={!hasActiveFilters} />
          </>
        )}
      >
        <InvDataGrid
          embedded
          fillHeight
          rows={items}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          getRowId={categoryRowId}
          emptyMessage="No categories yet"
          emptyDescription="Create your first category to start classifying inventory items. You can add more details when editing."
          emptyAction={<InvButton startIcon={<AddIcon />} onClick={openAddModal}>Add Category</InvButton>}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          pageSizeOptions={[pageSize]}
        />
      </MasterListShell>

      <MasterFormDialog
        open={addOpen}
        title="Add Category"
        subtitle="Enter a unique name and code. Additional settings can be configured after creation."
        formId="add-category-form"
        onSubmit={handleSubmit(onSubmit)}
        onClose={closeAddModal}
        saving={saving}
        submitLabel="Add Category"
        submitIcon={<AddIcon />}
      >
        <Controller
          name="category_name"
          control={control}
          rules={{ required: 'Category name is required' }}
          render={({ field }) => (
            <InvTextField {...field} label="Category Name *" error={errors.category_name} autoFocus />
          )}
        />
        <Controller
          name="category_code"
          control={control}
          rules={{ required: 'Category code is required' }}
          render={({ field }) => (
            <InvTextField
              {...field}
              label="Category Code *"
              error={errors.category_code}
              helperText="Short unique identifier (auto-uppercased)"
              onChange={e => field.onChange(e.target.value.toUpperCase())}
            />
          )}
        />
        <Controller
          name="item_class_id"
          control={control}
          rules={{ required: 'Item class is required' }}
          render={({ field }) => (
            <InvSelect
              label="Item Class *"
              value={field.value}
              onChange={field.onChange}
              options={itemClassOptions}
              error={errors.item_class_id}
            />
          )}
        />
      </MasterFormDialog>

      <Dialog
        open={Boolean(editTarget)}
        onClose={editSaving ? undefined : closeEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: cardSx }}
      >
        <DialogTitle sx={{
          color: IPD_COLORS.textPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}>
          Edit Category
          <IconButton onClick={closeEditModal} disabled={editSaving} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            id="edit-category-form"
            onSubmit={handleEditSubmit(onEditSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
          >
            <InvTextField label="Code" value={editTarget?.category_code || ''} disabled />
            <InvTextField label="Type" value={editTarget?.category_type || ''} disabled />
            <InvTextField label="Item Class" value={resolveItemClassName(editTarget)} disabled />
            <InvTextField
              label="Material Group"
              value={editTarget?.material_group_name || '—'}
              disabled
            />
            <InvTextField
              label="Parent Category"
              value={resolveParentLabel(editTarget, allCategories, {
                idKey: 'category_id',
                nameKey: 'category_name',
                parentKey: 'parent_category_id',
              })}
              disabled
            />
            <InvTextField
              label="Drug Category"
              value={editTarget?.is_drug_category ? 'Yes' : 'No'}
              disabled
            />
            <InvTextField
              label="Tracking defaults"
              value={[
                editTarget?.batch_required_default && 'Batch',
                editTarget?.expiry_required_default && 'Expiry',
                editTarget?.serial_required_default && 'Serial',
              ].filter(Boolean).join(', ') || 'None'}
              disabled
            />
            <Controller
              name="category_name"
              control={editControl}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvTextField {...field} label="Name *" error={editErrors.category_name} />
              )}
            />
            <Controller
              name="description"
              control={editControl}
              render={({ field }) => (
                <InvTextField {...field} label="Description" multiline rows={2} />
              )}
            />
            <SwitchField name="is_active" label="Active" control={editControl} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" onClick={closeEditModal} disabled={editSaving}>
            Cancel
          </InvButton>
          <InvButton type="submit" form="edit-category-form" loading={editSaving}>
            Save Changes
          </InvButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate Category"
        description={`Deactivate "${deactivateTarget?.category_name}"? It will no longer be available for new items.`}
        destructive
        loading={deactivating}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />
    </MaterialMasterShell>
  );
}
